import {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  lazy,
  Suspense,
  useRef,
} from "react";
import { InputGroup } from "react-bootstrap";
import Select from "react-select";
import { ArrayHelper, DateHelper, DEFAULT_PAGINATION_VALUES } from "utils";
import { Transacciones, ESTATUS_COMPROBANTE_ACEPTADO, ESTATUS_COMPROBANTE_ACEPTADO_CONDICIONAL } from "models/ECF/Transacciones";
import { TransaccionesManager } from "managers";
import ToolTip from "components/ToolTip/ToolTip";
import { GridView } from "components/GridView/GridView";
import IconButton from "components/IconButton/IconButton";
import useExceptionHandler from "hooks/useExceptionHandler";
import { AutoComplete, SvgComponent } from "components";
import SuspenseECF from "components/Suspense/SuspenseECF";
const Simplebar = lazy(() => import("components/Simplebar/Simplebar"));
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import {
  buttonStyle,
  controlsProportion,
} from "..";
import "../Lookup.style.scss";

export const TransaccionesLookup = forwardRef(
  (
    {
      isDisabled,
      idSecList = [],
      isInput = false,
      isMultiselect = false,
      onChange = () => { },
      onMultiselectChange = () => { },
      initialValue = { label: "", value: "" },
      initialValueList = [],
      limpiar = false,
      required = false,
      loadOnShowModal = false,
      loadValue = false,
    },
    ref
  ) => {
    const initialWhere = {
      EsPaginable: true,
      EstadoECFList: `${ESTATUS_COMPROBANTE_ACEPTADO},${ESTATUS_COMPROBANTE_ACEPTADO_CONDICIONAL}`,
      CantidadPorPagina: 5,
      EsLookup: true,
    };

    const initialWhereModal = {
      EstadoECFList: `${ESTATUS_COMPROBANTE_ACEPTADO},${ESTATUS_COMPROBANTE_ACEPTADO_CONDICIONAL}`,
      OrderBy: "IdSec",
    };

    const isMounted = useRef(true);
    const [showCanvas, setShowCanvas] = useState(false);
    const [transaccionListModal, setTransaccionListModal] = useState([]);
    const [whereModal, setWhereModal] = useState(initialWhereModal);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
    const [selectedValue, setSelectedValue] = useState({
      label: "",
      value: ""
    });
    const [multiSelectedValue, setMultiSelectedValue] = useState([]);

    const ESTADO_ECF_LIST = [
      { value: `${ESTATUS_COMPROBANTE_ACEPTADO},${ESTATUS_COMPROBANTE_ACEPTADO_CONDICIONAL}`, label: "Todos" },
      ...Transacciones.ESTADO_ECF_LIST.filter(
        (o) => o.value === ESTATUS_COMPROBANTE_ACEPTADO || o.value === ESTATUS_COMPROBANTE_ACEPTADO_CONDICIONAL
      ),
    ];

    const { handleException } = useExceptionHandler();
    const { setPaginationValuesWhere } = usePaginationValuesWhere();

    useEffect(() => {
      return () => {
        isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (limpiar) {
        handleMultiselectChange([]);
      }
    }, [limpiar]);

    useEffect(() => {
      if (!loadOnShowModal) {
        buscar();
      }
    }, [whereModal]);

    useEffect(() => {
      if (
        initialValue &&
        initialValue.value &&
        initialValue.value !== selectedValue.value
      ) {
        if (loadValue && !initialValue.label) buscarPorId(initialValue.value);
        else setSelectedValue(initialValue);
      }
    }, [initialValue.value]);

    useEffect(() => {
      if (initialValueList && initialValueList.length > 0) {
        setMultiSelectedValue(initialValueList);
      }
    }, [initialValueList.length]);

    const handleMultiselectChange = (value) => {
      setMultiSelectedValue(value ? value : []);
      onMultiselectChange(value ? value : []);
    };

    const handleSelectChange = (value) => {
      setSelectedValue(value ? value : {});
      onChange(value ? value : {});
    };

    function buscar() {
      const whereWithPaginationValues = {
        EstadoECFList: `${ESTATUS_COMPROBANTE_ACEPTADO},${ESTATUS_COMPROBANTE_ACEPTADO_CONDICIONAL}`,
        ...whereModal,
        ...pagination,
        IdSecList: idSecList,
      };
      TransaccionesManager
        .listado(whereWithPaginationValues)
        .then((res) => {
          if (isMounted.current) {
            setPaginationValuesWhere(res.Headers, pagination, setPagination);
            const newList = res.Data.map(t => {
              const transaccionDetallada = { ...t };
              if (t.Fecha) {
                transaccionDetallada.Fecha = DateHelper.formatStringDate(t.Fecha, false);
              }
              if (t.FechaCreacion) {
                transaccionDetallada.FechaCreacion = DateHelper.formatStringDate(t.FechaCreacion, true);
              }
              return transaccionDetallada;
            });
            setTransaccionListModal(newList);
          }
        })
        .catch((err) => handleException(err));
    }

    const buscarPorId = (idsec) => {
      TransaccionesManager
        .buscarPorId({ IdSec: idsec, EsLookup: true })
        .then((res) => {
          setSelectedValue({ label: res.label, value: res.value });
        })
        .catch((err) => handleException(err));
    };

    const handleCerrar = () => {
      setShowCanvas(false);
      setWhereModal(initialWhereModal);
    };

    const onPageIndexChanging = (pageNumber) => {
      setPagination((prevstate) => {
        return { ...prevstate, IndiceDePagina: pageNumber - 1 };
      });
      setWhereModal({ ...whereModal });
    };

    const getSuggestions = async (value) => {
      const inputValue = value.trim().toLowerCase();
      const res = await TransaccionesManager
        .listado({
          ...initialWhere,
          ENCF: inputValue,
          IdSecList: idSecList,
          EsLookup: true
        });
      return res.Data.map((t) => ({
        ...t,
        Fecha: t.Fecha ? DateHelper.formatStringDate(t.Fecha, false) : null,
      }));
    };

    const addToList = (newItem) => {
      setMultiSelectedValue((prev) => (prev ? [...prev, newItem] : [newItem]));
    };

    useImperativeHandle(ref, () => ({
      addToList,
    }));

    const renderInputContent = () => (
      <>
        <div style={{ width: isInput ? controlsProportion.inputGroup : controlsProportion.input }}>
          <AutoComplete
            required={required}
            isDisabled={isDisabled}
            getList={getSuggestions}
            noBorderRadius={isInput}
            isMultiselect={isMultiselect}
            placeholder={isMultiselect ? "Transacciones" : "Transacción"}
            parentValue={isMultiselect ? multiSelectedValue : selectedValue}
            setParentValue={
              isMultiselect ? handleMultiselectChange : handleSelectChange
            }
          />
        </div>
        {!isDisabled && (
          <InputGroup.Append style={{ width: controlsProportion.button }}>
            <AnimatedIconButton
              size="40px"
              height="18"
              width="18"
              message="Seleccionar Transacción"
              style={buttonStyle}
              disabled={isDisabled}
              iconName="search"
              onClick={() => {
                setShowCanvas(true);
                setTimeout(() => {
                  buscar();
                }, 200);
              }}
            />
          </InputGroup.Append>
        )}
      </>
    );

    return (
      <>
        {!isInput ? (
          <InputGroup className="w-100">
            {renderInputContent()}
          </InputGroup>
        ) : (
          renderInputContent()
        )}

        {showCanvas && (
          <Suspense fallback={<SuspenseECF />}>
            <Simplebar
              backdrop={true}
              placement={"end"}
              portal={false}
              size="xxl"
              useLoading={true}
              visible={showCanvas}
              setShow={(e) => setShowCanvas(e)}
              title="Seleccionar Transacción"
              btnLeft={{ show: false, func: () => { } }}
              btnRight={{ show: false, func: () => { } }}
            >
              <GridView
                pagination={pagination}
                setPagination={setPagination}
                allowPaging={pagination.EsPaginable}
                pageSize={pagination.CantidadPorPagina}
                pageIndex={pagination.IndiceDePagina}
                lastPageNumber={pagination.PaginasTotal}
                dataSource={transaccionListModal}
                onPageIndexChanging={onPageIndexChanging}
                where={whereModal}
                setWhere={setWhereModal}
              >
                <GridView.Column
                  isSticky
                  bodyRenderer={(row) => (
                    <div className="float-right">
                      <IconButton
                        onClick={() => {
                          let values = multiSelectedValue || [];

                          if (isMultiselect) {
                            handleMultiselectChange(
                              ArrayHelper.removeObjectsDuplicated(
                                [
                                  ...values,
                                  {
                                    label: row.Nombre,
                                    value: row.IdSec
                                  }
                                ],
                                ["value", "label"]
                              )
                            );
                          } else {
                            handleSelectChange({
                              label: row.ENCF,
                              value: row.IdSec,
                              ENCF: row.ENCF,
                              Fecha: row.Fecha,
                              TipoPago: row.TipoPago,
                              DocumentoComprador: row.DocumentoComprador,
                              RazonSocialComprador: row.RazonSocialComprador,
                            });
                          }
                          handleCerrar();
                        }}
                      >
                        <ToolTip message="Seleccionar">
                          <div>
                            <SvgComponent height="20" width="20" iconName="check" />
                          </div>
                        </ToolTip>
                      </IconButton>
                    </div>
                  )}
                />
                <GridView.Column
                  headerText="Id"
                  dataField="IdSec"
                  sortExpression="IdSec"
                  width="75px"
                />
                <GridView.Column
                  date
                  hideFilter
                  headerText="Fecha"
                  dataField="Fecha"
                  sortExpression="FechaEmision"
                  width="250px"
                  filterRenderer={(onInputChange) => {
                    return (
                      <DateTimePicker
                        soloFecha
                        esRangoFechas
                        esGrid
                        usarLabel={false}
                        fechaInicioOriginal={whereModal?.RangoFechas?.fechaInicio || ""}
                        fechaFinOriginal={whereModal?.RangoFechas?.fechaFin || ""}
                        onChange={(e) =>
                          onInputChange({
                            target: { name: "RangoFechas", value: e },
                          })
                        }
                      />
                    );
                  }}
                />
                <GridView.Column
                  headerText="E-CF"
                  dataField="ENCF"
                  sortExpression="eNCF"
                  width="180px"
                />
                <GridView.Column
                  headerText="Id Interno"
                  dataField="NumeroFacturaInterna"
                  sortExpression="NumeroFacturaInterna"
                  width="250px"
                />
                <GridView.Column
                  headerText="Estado e-CF"
                  dataField="EstadoECF"
                  sortExpression="EstadoECF"
                  width="180px"
                  filterRenderer={() => {
                    return (
                      <Select
                        className="react-select primary"
                        options={ESTADO_ECF_LIST}
                        value={ESTADO_ECF_LIST.filter(function (option) {
                          return (
                            option.value ===
                            (whereModal.EstadoECFList ?? ESTADO_ECF_LIST[0].value)
                          );
                        })}
                        name="EstadoECFList"
                        onChange={(selectedOption) =>
                          setWhereModal({
                            ...whereModal,
                            EstadoECFList: selectedOption.value,
                          })
                        }
                      />
                    );
                  }}
                />
                <GridView.Column
                  headerText="RNC Comprador"
                  dataField="RNCComprador"
                  sortExpression="RNCComprador"
                  width="400px"
                />
                <GridView.Column
                  headerText="Razon Social Comprador"
                  dataField="RazonSocialComprador"
                  sortExpression="RazonSocialComprador"
                  width="400px"
                />
                <GridView.Column
                  headerText="Monto Total"
                  dataField="MontoTotal"
                  sortExpression="MontoTotal"
                  width="180px"
                  number={true}
                />
                <GridView.Column
                  date
                  hideFilter
                  headerText="Fecha Creación"
                  dataField="FechaCreacion"
                  sortExpression="FechaCreacion"
                  width="250px"
                  filterRenderer={(onInputChange) => {
                    return (
                      <DateTimePicker
                        soloFecha
                        esRangoFechas
                        esGrid
                        usarLabel={false}
                        fechaInicioOriginal={
                          whereModal?.RangoFechasCreacion?.fechaInicio || ""
                        }
                        fechaFinOriginal={whereModal?.RangoFechasCreacion?.fechaFin || ""}
                        onChange={(e) =>
                          onInputChange({
                            target: { name: "RangoFechasCreacion", value: e },
                          })
                        }
                      />
                    );
                  }}
                />
              </GridView>
            </Simplebar>
          </Suspense>
        )}
      </>
    );
  }
);
